// frontend/src/utils/relationships/relationshipOntology.js
// Owns Daisy's canonical relationship semantics. Persistence adapters may
// translate these values, but the meaning of a relationship belongs here.

import relationshipRoles from '../relationshipRoles';

function normalizeAscii(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[‘’]/g, "'")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeRelationKey(value = '') {
  return normalizeAscii(value)
    .replace(/[-\s]+/g, '_')
    .replace(/_+/g, '_');
}

const LEGACY_DECLARATION_TERMS = [
  'mother', 'mom', 'father', 'dad', 'son', 'daughter', 'brother', 'sister',
  'friend', 'buddy', 'pal', 'wife', 'husband', 'girlfriend', 'boyfriend',
  'ex-wife', 'ex-husband', 'ex-girlfriend', 'ex-boyfriend',
  'coworker', 'coworkers', 'colleague', 'colleagues', 'boss', 'manager',
  'employee', 'employees', 'grandmother', 'grandfather', 'grandma', 'grandpa',
  'aunt', 'uncle', 'cousin', 'parent', 'parents', 'child', 'children',
  'grandparent', 'grandparents', 'granddaughter', 'grandson', 'grandchild', 'grandchildren', 'niece', 'nephew',
  'partner', 'spouse', 'roommate', 'neighbor', 'neighbour', 'doctor', 'caregiver', 'vet', 'dog', 'cat', 'pet',
];

const NEW_DECLARATION_TERMS = ['fiance', 'fiancé', 'fiancee', 'fiancée'];

const RELATIONSHIP_TERM_NORMALIZATION = {
  mom: 'mother',
  mum: 'mother',
  dad: 'father',
  buddy: 'friend',
  pal: 'friend',
  grandma: 'grandmother',
  nana: 'grandmother',
  granny: 'grandmother',
  grandpa: 'grandfather',
  gramps: 'grandfather',
  coworkers: 'coworker',
  colleagues: 'coworker',
  colleague: 'coworker',
  manager: 'boss',
  employees: 'employee',
  parents: 'parent',
  children: 'child',
  grandparents: 'grandparent',
  grandchildren: 'grandchild',
  neighbour: 'neighbor',
  spouse: 'partner',
  fiance: 'fiance',
  fiancee: 'fiancee',
};

// Stability describes how readily current-state arbitration should expect this
// relationship class to change. It is evidence for Daisy, never truth by itself.
const GRAPH_RELATION_DEFINITIONS = {
  dog_of: { directionality: 'directed', requestRoles: ['dog'], stability: 'stable' },
  cat_of: { directionality: 'directed', requestRoles: ['cat'], stability: 'stable' },
  pet_of: { directionality: 'directed', requestRoles: ['pet'], stability: 'stable' },

  boyfriend_of: { directionality: 'directed', requestRoles: ['boyfriend'], stability: 'changeable' },
  girlfriend_of: { directionality: 'directed', requestRoles: ['girlfriend'], stability: 'changeable' },
  ex_boyfriend_of: { directionality: 'directed', requestRoles: ['ex-boyfriend'], stability: 'historical' },
  ex_girlfriend_of: { directionality: 'directed', requestRoles: ['ex-girlfriend'], stability: 'historical' },
  husband_of: { directionality: 'directed', requestRoles: ['husband'], stability: 'moderate' },
  wife_of: { directionality: 'directed', requestRoles: ['wife'], stability: 'moderate' },
  ex_husband_of: { directionality: 'directed', requestRoles: ['ex-husband'], stability: 'historical' },
  ex_wife_of: { directionality: 'directed', requestRoles: ['ex-wife'], stability: 'historical' },
  partner_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['partner', 'spouse'],
    stability: 'moderate',
  },
  engaged_to: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['fiance', 'fiancee'],
    aliases: ['fiance_of', 'fiancé_of', 'fiancee_of', 'fiancée_of', 'engaged_with', 'engaged_to'],
    stability: 'changeable',
  },

  father_of: { directionality: 'directed', requestRoles: ['father'], inverseRequestRoles: ['child'], stability: 'structural' },
  mother_of: { directionality: 'directed', requestRoles: ['mother'], inverseRequestRoles: ['child'], stability: 'structural' },
  parent_of: { directionality: 'directed', requestRoles: ['parent'], inverseRequestRoles: ['child'], stability: 'structural' },
  son_of: { directionality: 'directed', requestRoles: ['son'], inverseRequestRoles: ['parent'], stability: 'structural' },
  daughter_of: { directionality: 'directed', requestRoles: ['daughter'], inverseRequestRoles: ['parent'], stability: 'structural' },
  child_of: { directionality: 'directed', requestRoles: ['child'], inverseRequestRoles: ['parent'], stability: 'structural' },
  brother_of: { directionality: 'directed', requestRoles: ['brother'], stability: 'structural' },
  sister_of: { directionality: 'directed', requestRoles: ['sister'], stability: 'structural' },
  aunt_of: { directionality: 'directed', requestRoles: ['aunt'], stability: 'structural' },
  uncle_of: { directionality: 'directed', requestRoles: ['uncle'], stability: 'structural' },
  niece_of: { directionality: 'directed', requestRoles: ['niece'], stability: 'structural' },
  nephew_of: { directionality: 'directed', requestRoles: ['nephew'], stability: 'structural' },
  grandmother_of: { directionality: 'directed', requestRoles: ['grandmother'], inverseRequestRoles: ['grandchild'], stability: 'structural' },
  grandfather_of: { directionality: 'directed', requestRoles: ['grandfather'], inverseRequestRoles: ['grandchild'], stability: 'structural' },
  grandparent_of: { directionality: 'directed', requestRoles: ['grandparent'], inverseRequestRoles: ['grandchild'], stability: 'structural' },
  granddaughter_of: { directionality: 'directed', requestRoles: ['granddaughter'], inverseRequestRoles: ['grandparent'], stability: 'structural' },
  grandson_of: { directionality: 'directed', requestRoles: ['grandson'], inverseRequestRoles: ['grandparent'], stability: 'structural' },
  grandchild_of: { directionality: 'directed', requestRoles: ['grandchild'], inverseRequestRoles: ['grandparent'], stability: 'structural' },
  cousin_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['cousin'],
    stability: 'structural',
  },

  friend_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['friend'],
    stability: 'moderate',
  },
  coworker_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['coworker', 'colleague'],
    stability: 'changeable',
  },
  boss_of: { directionality: 'directed', requestRoles: ['boss', 'manager'], stability: 'changeable' },
  employee_of: { directionality: 'directed', requestRoles: ['employee'], stability: 'changeable' },
  roommate_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['roommate'],
    stability: 'changeable',
  },
  neighbor_of: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: ['neighbor'],
    stability: 'moderate',
  },
  doctor_for: { directionality: 'directed', requestRoles: ['doctor'], stability: 'contextual' },
  caregiver_for: { directionality: 'directed', requestRoles: ['caregiver'], stability: 'contextual' },
  vet_for: { directionality: 'directed', requestRoles: ['vet'], stability: 'contextual' },

  allergic_to: { directionality: 'directed', requestRoles: [], stability: 'stable' },
  associated_with: {
    directionality: 'reciprocal',
    canonicalizeEndpoints: true,
    requestRoles: [],
    stability: 'contextual',
  },
};

const GRAPH_RELATION_ALIAS_MAP = Object.entries(GRAPH_RELATION_DEFINITIONS)
  .reduce((map, [canonicalRelation, definition]) => {
    map[normalizeRelationKey(canonicalRelation)] = canonicalRelation;
    (definition.aliases || []).forEach((alias) => {
      map[normalizeRelationKey(alias)] = canonicalRelation;
    });
    return map;
  }, {});

function getRelationshipDeclarationTerms() {
  const ontologyTerms = relationshipRoles.flatMap((descriptor) => [
    descriptor?.keyword,
    descriptor?.role,
    ...(Array.isArray(descriptor?.aliases) ? descriptor.aliases : []),
  ]);

  return [...new Set(
    [...LEGACY_DECLARATION_TERMS, ...NEW_DECLARATION_TERMS, ...ontologyTerms]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];
}

function getRelationshipDeclarationScoreTerms() {
  return [
    'friend', 'brother', 'sister', 'mother', 'father', 'daughter', 'son',
    'wife', 'husband', 'girlfriend', 'boyfriend', 'fiance', 'fiancee',
    'cousin', 'aunt', 'uncle', 'grandmother', 'grandfather', 'grandma',
    'grandpa', 'grandparent', 'granddaughter', 'grandson', 'grandchild', 'niece', 'nephew',
    'coworker', 'colleague', 'boss', 'manager', 'employee',
    'child', 'parent', 'partner', 'roommate', 'neighbor', 'doctor', 'caregiver', 'vet',
    'dog', 'cat', 'pet',
  ];
}

function normalizeRelationshipTerm(value = '') {
  const normalized = normalizeAscii(value);
  if (!normalized) return '';

  const direct = RELATIONSHIP_TERM_NORMALIZATION[normalized];
  if (direct) return direct;

  const descriptor = getRelationshipRoleDescriptor(normalized);
  return normalizeAscii(descriptor?.keyword || descriptor?.role || normalized);
}

function getRelationshipRoleDescriptor(value = '') {
  const normalized = normalizeAscii(value);
  if (!normalized) return null;

  return relationshipRoles.find((descriptor) => {
    const values = [
      descriptor?.keyword,
      descriptor?.role,
      ...(Array.isArray(descriptor?.aliases) ? descriptor.aliases : []),
    ]
      .map(normalizeAscii)
      .filter(Boolean);

    return values.includes(normalized);
  }) || null;
}

function getRelationshipRoleGender(value = '') {
  const descriptor = getRelationshipRoleDescriptor(value);
  return descriptor?.gender || 'unknown';
}

function normalizeGraphRelation(value = '') {
  const relationKey = normalizeRelationKey(value);
  if (!relationKey) return '';
  return GRAPH_RELATION_ALIAS_MAP[relationKey] || relationKey;
}

function getGraphRelationDefinition(value = '') {
  const canonicalRelation = normalizeGraphRelation(value);
  const definition = GRAPH_RELATION_DEFINITIONS[canonicalRelation];
  if (!definition) return null;
  return { relation: canonicalRelation, ...definition };
}

function getGraphRelationForRelationshipRole(value = '') {
  const normalizedRole = normalizeRelationshipTerm(value);
  if (!normalizedRole) return '';

  const match = Object.entries(GRAPH_RELATION_DEFINITIONS).find(([, definition]) => {
    return (definition.requestRoles || [])
      .map(normalizeRelationshipTerm)
      .includes(normalizedRole);
  });

  return match?.[0] || '';
}

function isAllowedAdditiveGraphRelation(value = '') {
  return Boolean(getGraphRelationDefinition(value));
}

function valueToRoleKey(value = '') {
  return normalizeAscii(value).replace(/[-_]+/g, ' ');
}

function getGraphRelationRoleOrientation(relation = '', role = '') {
  const canonicalRelation = normalizeGraphRelation(relation);
  const normalizedRole = normalizeRelationshipTerm(valueToRoleKey(role));
  const definition = GRAPH_RELATION_DEFINITIONS[canonicalRelation];

  if (!definition || !normalizedRole) return null;

  const requestRoles = (definition.requestRoles || []).map(normalizeRelationshipTerm);
  const inverseRequestRoles = (definition.inverseRequestRoles || []).map(normalizeRelationshipTerm);

  const requestMatches = requestRoles.includes(normalizedRole) || (
    normalizedRole === 'pet' &&
    requestRoles.some((requestRole) => ['dog', 'cat', 'pet'].includes(requestRole))
  );

  if (definition.directionality === 'reciprocal' && requestMatches) return 'either';
  if (requestMatches) return 'from';
  if (inverseRequestRoles.includes(normalizedRole)) return 'to';
  return null;
}

function graphRelationMatchesRole(relation = '', role = '') {
  if (getGraphRelationRoleOrientation(relation, role)) return true;

  const canonicalRelation = normalizeGraphRelation(relation);
  const normalizedRole = normalizeRelationshipTerm(valueToRoleKey(role));
  const legacyRole = canonicalRelation.replace(/_(of|for)$/g, '');
  return legacyRole === normalizedRole;
}

function getEndpointSortKey({ entityId = null, name = '' } = {}) {
  const nameKey = normalizeAscii(name);
  const idKey = String(entityId || '').trim().toLowerCase();
  return `${nameKey}::${idKey}`;
}

function canonicalizeRelationshipEdge(edge = {}) {
  const relation = normalizeGraphRelation(edge?.relation || '');
  if (!relation) return { ...edge, relation: '' };

  const definition = GRAPH_RELATION_DEFINITIONS[relation];
  const normalizedEdge = { ...edge, relation };

  if (definition?.canonicalizeEndpoints !== true) return normalizedEdge;

  const fromKey = getEndpointSortKey({
    entityId: edge?.fromEntityId,
    name: edge?.fromName,
  });
  const toKey = getEndpointSortKey({
    entityId: edge?.toEntityId,
    name: edge?.toName,
  });

  if (!fromKey || !toKey || fromKey <= toKey) return normalizedEdge;

  return {
    ...normalizedEdge,
    fromName: edge?.toName,
    fromType: edge?.toType,
    fromEntityId: edge?.toEntityId || null,
    fromIsRoot: edge?.toIsRoot === true,
    toName: edge?.fromName,
    toType: edge?.fromType,
    toEntityId: edge?.fromEntityId || null,
    toIsRoot: edge?.fromIsRoot === true,
  };
}

function buildRelationshipEdgeKey(edge = {}) {
  const canonicalEdge = canonicalizeRelationshipEdge(edge);
  const fromKey = normalizeAscii(canonicalEdge?.fromName || '');
  const toKey = normalizeAscii(canonicalEdge?.toName || '');
  const relation = normalizeGraphRelation(canonicalEdge?.relation || '');
  if (!fromKey || !toKey || !relation) return '';
  return `${fromKey}::${relation}::${toKey}`;
}

function buildRelationshipEdgeIdentityKey(edge = {}) {
  const canonicalEdge = canonicalizeRelationshipEdge(edge);
  const relation = normalizeGraphRelation(canonicalEdge?.relation || '');
  const logicalKey = String(canonicalEdge?.logicalKey || '').trim();
  if (logicalKey) return logicalKey;

  const fromEntityId = String(canonicalEdge?.fromEntityId || '').trim();
  const toEntityId = String(canonicalEdge?.toEntityId || '').trim();
  if (fromEntityId && relation && toEntityId) {
    return `${fromEntityId}::${relation}::${toEntityId}`;
  }

  return buildRelationshipEdgeKey(canonicalEdge);
}

export {
  GRAPH_RELATION_DEFINITIONS,
  buildRelationshipEdgeIdentityKey,
  buildRelationshipEdgeKey,
  canonicalizeRelationshipEdge,
  getGraphRelationDefinition,
  getGraphRelationForRelationshipRole,
  getGraphRelationRoleOrientation,
  getRelationshipDeclarationScoreTerms,
  getRelationshipDeclarationTerms,
  getRelationshipRoleDescriptor,
  getRelationshipRoleGender,
  graphRelationMatchesRole,
  isAllowedAdditiveGraphRelation,
  normalizeGraphRelation,
  normalizeRelationshipTerm,
};

export default GRAPH_RELATION_DEFINITIONS;
